import moment from 'moment'
import { AssociatedFlexProjectModel } from '../models/flex/AssociatedFlexProject.model'
import {
  FlexSampleDetailModel,
  FlexSampleStatusModel,
} from '../models/flex/FlexSampleTemplate.models'
import { ResultModel } from '../models/result.model'
import PmrE2EProjectModel from '../pages/project-management-pmr/shared/interfaces/pmr-e2e-project-model'
import IProjectTaskStatusUpdateModel from '../pages/project-management-pmr/shared/interfaces/pmr-e2e-project-task-status-update.model'
import { OnHoldReasonType } from '../pages/project-management/shared/enums/OnHoldReasonType'
import { AdHocTaskFlexProjectAssociationOptionModel } from '../shared/models/AdHocTaskFlexProjectAssociationOption.model'
import { CreateAdHocTaskModel } from '../shared/models/CreateAdHocTaskModel'
import { CreateFlexTaskCommentModel } from '../shared/models/CreateFlexTaskComment.model.'
import { E2EProjectLineModel, E2EProjectModel } from '../shared/models/E2EProject.model'
import { FlexTaskCommentModel } from '../shared/models/FlexTaskDetail.model'
import { GetTaskStatusByTaskNameModel } from '../shared/models/GetTaskStatusByTaskName.model'
import { ProjectFullViewRequestModel } from '../shared/models/ProjectFullViewRequestModel'
import { UpdateProjectLineHandlerRequestModel } from '../shared/models/UpdateProjectLineHandlerRequest.model'
import { AddRemarksModel } from '../shared/models/service-models/AddRemarks.model'
import { BulkUpdateProjectAndProjectLineECDsModel } from '../shared/models/service-models/BulkUpdateProjectAndProjectLineECDs.model'
import {
  CoHandlerAssignmentModel,
  CoHandlerAssignmentRequestModel,
} from '../shared/models/service-models/CoHandlerAssignmentRequestModel.model'
import E2EProjectDetailsRequestModel from '../shared/models/service-models/E2EProjectDetailsRequest.model'
import { E2EProjectDetailsResultModel } from '../shared/models/service-models/E2EProjectDetailsResult.model'
import { E2EProjectLineTaskMilestoneRequestModel } from '../shared/models/service-models/E2EProjectLineMilestoneRequest.model'
import E2EProjectLineRequestModel from '../shared/models/service-models/E2EProjectLineRequest.model'
import E2EProjectRequestModel from '../shared/models/service-models/E2EProjectRequest.model'
import { EditGmaWbProjectRequestModel } from '../shared/models/service-models/EditGmaWbProjectRequest.model'
import {
  FlexTemplateConfirmationStatusModel,
  FlexTemplateConfirmationStatusRequestModel,
} from '../shared/models/service-models/FlexTemplateConfirmationStatus.model'
import { GetRemarksModel } from '../shared/models/service-models/GetRemarks.model'
import { GetTaskStatusByTaskNameResultModel } from '../shared/models/service-models/GetTaskStatusByTaskNameResult.model'
import { HandlerAssignmentMailDraftModel } from '../shared/models/service-models/HandlerAssignmentMailDraft.model'
import { OnHoldReasonsModel } from '../shared/models/service-models/OnHoldReasons.model'
import { PaginatedResultModel } from '../shared/models/service-models/PaginatedResult.model'
import { PmrBulkEditEcdResultModel } from '../shared/models/service-models/PmrBulkEditEcdResult.model'
import { PmrBulkEditFlexProjectEcdRequest } from '../shared/models/service-models/PmrBulkEditFlexProjectEcdRequest.model'
import { PmrBulkEditOrderlineEcdRequest } from '../shared/models/service-models/PmrBulkEditOrderlineEcdRequest.model'
import { SaveProjectLineHandlerAssignmentMailDraftModel } from '../shared/models/service-models/SaveProjectLineHandlerAssignmentMailDraft.model'
import {
  TimestampProjectLineFlexTasksConfirmationModel,
  TimestampProjectLineMilestonesConfirmationModel,
} from '../shared/models/service-models/TimestampProjectLineMilestones.model'
import { UpdateProjectECDModel } from '../shared/models/service-models/UpdateProjectECDModel.model'
import { UpdateProjectECDsModel } from '../shared/models/service-models/UpdateProjectECDsModel'
import { UpdateProjectlineECDModel } from '../shared/models/service-models/UpdateProjectlineECDModel.model'
import RequestFactory from './RequestFactory'
import {
  ManilaCoeTrackerDetailsModel,
  ManilaCoeTrackerUpdateModel,
} from '../shared/models/service-models/ManilaCoeTrackerDetailsModel'

const GMAE2EURL = process.env.REACT_APP_GMAE2E_URL

const ProjectService = {
  getProjects: async (request: E2EProjectRequestModel, cancelSource: any = undefined) => {
    return (await RequestFactory.post(GMAE2EURL, '/api/project/get-projects', request, () => {
      if (cancelSource === undefined) return undefined

      return {
        cancelToken: cancelSource.token,
      } as any
    })) as Promise<ResultModel<PaginatedResultModel<PmrE2EProjectModel[]>>>
  },
  getProjectsPmr: async (request: E2EProjectRequestModel, cancelSource: any = undefined) => {
    return (await RequestFactory.post(
      GMAE2EURL,
      '/api/project/get-projects-pmr-v2',
      request,
      () => {
        if (cancelSource === undefined) return undefined

        return {
          cancelToken: cancelSource.token,
        } as any
      }
    )) as Promise<ResultModel<PaginatedResultModel<PmrE2EProjectModel[]>>>
  },
  getProjectList: async (request: E2EProjectRequestModel) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-project-list', request),
  getProjectTasksByProject: async (request: any, cancelSource: any = undefined) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-project-tasks', request, () => {
      if (cancelSource === undefined) return undefined

      return {
        cancelToken: cancelSource.token,
      } as any
    }),
  getProjectListByCustomer: async (accountNo?: string | null, partySiteNumber?: string | null) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-project-list-by-customer?accountNumber=${accountNo}&partySiteNumber=${partySiteNumber}`
    )) as ResultModel<Array<E2EProjectModel>>,
  getProjectLineList: async (request: E2EProjectLineRequestModel) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-project-line-list', request),
  getProjectDetails: async (request: E2EProjectDetailsRequestModel) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/project-details?projectId=${request.projectId}&isFlexTaskOnly=${request.isFlexTaskOnly}`
    )) as E2EProjectDetailsResultModel,
  getCertificateSchemeList: async () =>
    await RequestFactory.get(GMAE2EURL, '/api/project/get-certificate-scheme-list'),
  getE2ETemplateByFlexProject: async (flexProjectId?: string | null) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-e2e-template-by-flex-project?flexProjectId=${flexProjectId}`
    ),
  getE2ETemplate: async (request?: E2EProjectLineModel) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-e2e-template?certificateSchemeId=${request?.schemeId}&applicationTypeId=${request?.applicationTypeId}&deliveryPathId=${request?.deliveryPathId}&additionalInfoId=${request?.additionalInfoId}&flexProjectId=${request?.flexProjectId}`
    ),
  createProject: async (request: E2EProjectModel) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/create-gmawb-project', request),
  addProjectLines: async (request: E2EProjectModel) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/add-project-lines', request),
  validateE2EProjectName: async (projectNameRequest: { projectName: string }) =>
    await RequestFactory.post(
      GMAE2EURL,
      '/api/project/validate-e2e-project-name',
      projectNameRequest
    ),
  constructProjectName: (applicantName: string): string => {
    const timestamp = moment(new Date()).format('MMDDyyyy')

    return applicantName + '_' + timestamp
  },
  getProjectOrderline: async (request: any, cancelSource: any = undefined) =>
    await RequestFactory.post(
      GMAE2EURL,
      `/api/project/get-project-task-orderlines`,
      request,
      () => {
        if (cancelSource === undefined) return undefined

        return {
          cancelToken: cancelSource.token,
        } as any
      }
    ),
  getProjectMilestoneTask: async (request: any, cancelSource: any = undefined) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-project-milestones', request, () => {
      if (cancelSource === undefined) return undefined

      return {
        cancelToken: cancelSource.token,
      } as any
    }),
  getGMAWBFlexProjects: async (projectId: string) =>
    await RequestFactory.get(GMAE2EURL, `/api/project/get-gmawb-flex-projects/${projectId}`),
  getGMAWBFlexProjectDetails: async (flexProjectId: string, projectId: string) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-gmawb-flex-project-details?flexProjectId=${flexProjectId}&projectId=${projectId}`
    ),
  getProjectDetailsFlexDetails: async (projectId: string) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-flex-project-details?flexProjectId=${projectId}`
    ),
  getOrderLineListDetails: async (projectId: string) =>
    await RequestFactory.get(GMAE2EURL, `/api/project/get-orderlines-details/${projectId}`),
  getTaskOnHoldStatus: async () =>
    await RequestFactory.get(GMAE2EURL, `/api/flex-integration/get-task-hold-reasons`),
  updateOrderlineFlexTaskStatus: async (data: any) =>
    await RequestFactory.post(GMAE2EURL, `/api/project/update-flex-task-orderline-status`, data),
  updateMilestoneStatus: async (data: any) =>
    await RequestFactory.post(GMAE2EURL, `/api/project/update-milestone-status`, data),
  updateOrderlineStatus: async (data: any) =>
    await RequestFactory.post(GMAE2EURL, `/api/project/update-projectlines-statuses`, data),
  updateOrderlineECD: async (data: UpdateProjectlineECDModel) =>
    (await RequestFactory.post(
      GMAE2EURL,
      `/api/project/update-projectline-ecd`,
      data
    )) as Promise<ResultModel>,
  updateProjectECD: async (data: UpdateProjectECDModel) =>
    (await RequestFactory.post(
      GMAE2EURL,
      `/api/project/update-project-ecd`,
      data
    )) as Promise<ResultModel>,
  getProjectContributorsList: async (projectId: string) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-project-collaborators?projectId=${projectId}`
    ),
  deleteE2EProject: async (projectId: string) =>
    await RequestFactory.delete(GMAE2EURL, `/api/project/delete-project/${projectId}`),
  getEditProjectDetails: async (projectId: string) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-edit-project-details?e2eProjectId=${projectId}`
    ),
  saveEditProjectDetails: async (request: EditGmaWbProjectRequestModel) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/save-edit-project-details', request),
  updateProjectLineIsMismatchECD: async (e2EProjectLineId: string) =>
    (await RequestFactory.patch(
      GMAE2EURL,
      `/api/project/update-projectline-is-mismatch-ecd/${e2EProjectLineId}`,
      null
    )) as Promise<ResultModel>,
  checkPermissionToRemoveOrderLines: async (request: Array<string>) =>
    await RequestFactory.post(
      GMAE2EURL,
      '/api/project/check-permission-remove-order-lines',
      request
    ),
  removeOrderLines: async (request: Array<string>) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/remove-order-lines', request),
  computeAdjustedEcdOnEditProject: async (request: E2EProjectModel) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/compute-adjusted-ecd-edit-project', request),

  addRemarks: async (request: AddRemarksModel) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/create-remark', request),

  getRemarks: async (request: GetRemarksModel) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-remark?e2EProjectLineId=${request.e2EProjectLineId}&projectLineRemarksType=${request.projectLineRemarksType}`,
      request
    ),
  getE2EProjectLines: async (e2eProjectId: string) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-e2e-project-lines?e2eProjectId=${e2eProjectId}`
    )) as Promise<ResultModel<E2EProjectLineModel[]>>,
  getAdHocTaskFlexProjectAssociationOptions: async (
    e2eProjectId: string,
    flexCorrelationId: string | null
  ) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-ad-hoc-task-flex-project-association-options?e2eProjectId=${e2eProjectId}&flexCorrelationId=${flexCorrelationId}`
    )) as Promise<ResultModel<AdHocTaskFlexProjectAssociationOptionModel[]>>,
  getE2EProjectLinesCommonTasks: async (e2eProjectLineIds: string[]) =>
    (await RequestFactory.post(
      GMAE2EURL,
      '/api/project/get-e2e-project-lines-common-active-tasks',
      e2eProjectLineIds
    )) as Promise<ResultModel<string[]>>,
  getProjectDistinctMilestoneNames: async (e2eProjectId: string) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-project-distinct-milestone-names?e2eProjectId=${e2eProjectId}`
    )) as Promise<ResultModel<string[]>>,
  createAdHocTask: async (model: CreateAdHocTaskModel) =>
    (await RequestFactory.post(
      GMAE2EURL,
      '/api/project/create-ad-hoc-task',
      model
    )) as Promise<ResultModel>,
  createFlexTaskComment: async (
    model: CreateFlexTaskCommentModel
  ): Promise<ResultModel<FlexTaskCommentModel>> =>
    await RequestFactory.post(GMAE2EURL, '/api/project/create-flex-task-comment', model),
  canUserAddE2EProjectLineFlexTaskComment: async (
    flexProjectTaskId: string
  ): Promise<ResultModel<boolean>> =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/can-user-add-e2e-project-line-flex-task-comment?flexProjectTaskId=${flexProjectTaskId}`
    ),
  setProjectFullViewCacheKey: async (payload: ProjectFullViewRequestModel): Promise<ResultModel> =>
    await RequestFactory.post(GMAE2EURL, '/api/project/set-cache-project-full-view-key', payload),

  getProjectFullViewCacheKey: async (cacheKey: string): Promise<ResultModel<string>> =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-cache-project-full-view-key?cacheKey=${cacheKey}`
    ),
  updateProjectLineHandler: async (
    payload: UpdateProjectLineHandlerRequestModel
  ): Promise<ResultModel> =>
    await RequestFactory.post(GMAE2EURL, '/api/project/update-projectline-handler', payload),
  downloadStatusReport: async (requestModel: any) =>
    await RequestFactory.download(GMAE2EURL, `/api/project/get-status-report`, requestModel),
  getIsMilestonePredecessorsCompleted: async (
    e2EProjectLineMilestoneId: string
  ): Promise<ResultModel<boolean>> =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-is-milestone-predecessors-completed?e2EProjectLineMilestoneId=${e2EProjectLineMilestoneId}`
    ),

  checkIsMilestoneHasTimeEntry: async (
    e2EProjectLineMilestoneId: string
  ): Promise<ResultModel<boolean>> =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/check-is-milestone-has-timeentry?e2EProjectLineMilestoneId=${e2EProjectLineMilestoneId}`
    ),

  getMilestoneList: async (
    payload: E2EProjectLineTaskMilestoneRequestModel
  ): Promise<ResultModel<TimestampProjectLineMilestonesConfirmationModel[]>> =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-project-milestone-list', payload),

  getOnHoldReasonList: async (type: OnHoldReasonType): Promise<ResultModel<OnHoldReasonsModel[]>> =>
    await RequestFactory.get(GMAE2EURL, `/api/project/get-hold-reason?type=${type}`),

  flexTaskStatusUpdate: async (payload: IProjectTaskStatusUpdateModel): Promise<ResultModel> =>
    await RequestFactory.post(
      GMAE2EURL,
      '/api/project/update-flex-task-orderline-status-v2',
      payload
    ),

  updateProjectECDs: async (data: UpdateProjectECDsModel) =>
    (await RequestFactory.post(
      GMAE2EURL,
      `/api/project/update-project-ecds`,
      data
    )) as Promise<ResultModel>,
  bulkUpdateProjectAndProjectLineECDs: async (data: BulkUpdateProjectAndProjectLineECDsModel) =>
    (await RequestFactory.post(
      GMAE2EURL,
      `/api/project/bulk-update-project-and-project-line-ecds`,
      data
    )) as Promise<ResultModel>,
  getRequireInvoicePrice: async (request: any, cancelSource: any = undefined) =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-require-invoice-price', request, () => {
      if (cancelSource === undefined) return undefined

      return {
        cancelToken: cancelSource.token,
      } as any
    }),
  getProjectLineFlexTaskList: async (
    payload: E2EProjectLineTaskMilestoneRequestModel
  ): Promise<ResultModel<TimestampProjectLineFlexTasksConfirmationModel[]>> =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-project-flex-task-list', payload),

  getTasksStatus: async (
    payload: GetTaskStatusByTaskNameModel
  ): Promise<GetTaskStatusByTaskNameResultModel[]> =>
    await RequestFactory.post(GMAE2EURL, '/api/project/get-tasks-status', payload),

  getLeadTime: async (request: any) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-lead-time?schemeId=${request.schemeId}&deliveryPathId=${
        request.deliveryPathId
      }&applicationTypeId=${request.applicationTypeId}&additionalInfoId=${
        request.additionalInfoId ?? ''
      }&leadTypeId=${request.leadTypeId}&flexCorrellationId=${
        request.projectTemplateCorellationId ?? request.flexCorrelationId
      }`
    ),

  getTaskMilestoneRequiredHandlerAssignment: async (
    payload: CoHandlerAssignmentRequestModel
  ): Promise<ResultModel<CoHandlerAssignmentModel[]>> =>
    await RequestFactory.post(
      GMAE2EURL,
      '/api/project/get-tasks-milestones-required-handler-assignment',
      payload
    ),

  bulkUpdateOrderlineEcd: async (
    data: PmrBulkEditOrderlineEcdRequest[]
  ): Promise<PmrBulkEditEcdResultModel> =>
    await RequestFactory.post(GMAE2EURL, `/api/project/bulk-update-orderline-ecd`, data),

  bulkUpdateFlexProjectEcd: async (
    data: PmrBulkEditFlexProjectEcdRequest[]
  ): Promise<PmrBulkEditEcdResultModel> =>
    await RequestFactory.post(GMAE2EURL, `/api/project/bulk-update-flex-project-ecd`, data),

  getFlexOrderLineCount: async (e2eProjectId: string) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-flex-orderline-count?e2eProjectId=${e2eProjectId}`
    ),

  getOrderlineModels: async (e2eProjectLineId: string) =>
    await RequestFactory.get(GMAE2EURL, `/api/project/get-orderline-models/${e2eProjectLineId}`),

  getFlexTemplateConfirmationStatus: async (
    data: FlexTemplateConfirmationStatusRequestModel
  ): Promise<ResultModel<FlexTemplateConfirmationStatusModel[]>> =>
    await RequestFactory.post(
      GMAE2EURL,
      `/api/project/get-flex-template-confirmation-status`,
      data
    ),

  getAssociatedFlexProjects: async (projectId: string) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/get-associated-flex-projects?flexProjectId=${projectId}`
    )) as Promise<ResultModel<AssociatedFlexProjectModel[]>>,

  getValidHandlers: async () =>
    (await RequestFactory.get(GMAE2EURL, `/api/project/valid-handlers`)) as Promise<
      ResultModel<string>
    >,

  getFlexSampleStatus: async (projectId: string) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/myul/flex-sample-status?projectId=${projectId}`
    )) as Promise<ResultModel<FlexSampleStatusModel>>,

  getFlexSampleDetail: async (projectId: string) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/myul/flex-sample-detail?projectId=${projectId}`
    )) as Promise<ResultModel<FlexSampleDetailModel>>,

  saveHandlerAssignmentMailDraft: async (draft: SaveProjectLineHandlerAssignmentMailDraftModel) =>
    (await RequestFactory.post(
      GMAE2EURL,
      `/api/project/e2e-project-lines/save-handler-assignment-mail-draft`,
      draft
    )) as Promise<ResultModel<HandlerAssignmentMailDraftModel>>,
  discardHandlerAssignmentMailDraft: async (id: string) =>
    await RequestFactory.delete(
      GMAE2EURL,
      `/api/project/e2e-project-lines/discard-handler-assignment-mail-draft?id=${id}`
    ),
  getHandlerAssignmentMailDraft: async (id: string, includeProjectLines: boolean) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/e2e-project-lines/get-handler-assignment-mail-draft?id=${id}&includeProjectLines=${includeProjectLines}`
    )) as Promise<ResultModel<HandlerAssignmentMailDraftModel>>,
  getManilaCoeTrackerDetails: async (e2eProjectLineId: string) =>
    (await RequestFactory.get(
      GMAE2EURL,
      `/api/project/project-line-coe-details?e2eProjectLineId=${e2eProjectLineId}`
    )) as Promise<ResultModel<ManilaCoeTrackerDetailsModel>>,
  postManilaCoeTrackerDetails: async (model: ManilaCoeTrackerDetailsModel) =>
    (await RequestFactory.post(
      GMAE2EURL,
      `/api/project/project-line-coe-details`,
      model
    )) as Promise<ResultModel<ManilaCoeTrackerDetailsModel>>,
  updateCoeTrackerDetails: async (model: ManilaCoeTrackerUpdateModel) =>
    (await RequestFactory.patch(
      GMAE2EURL,
      `/api/project/project-line-coe-details`,
      model
    )) as Promise<ResultModel>,
  deleteCoeTrackerDetails: async (id: string) =>
    await RequestFactory.delete(GMAE2EURL, `/api/project/project-line-coe-details?id=${id}`),
}

export default ProjectService
